<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Enter Lab Results</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('lab-dashboard')">Laboratory</a></li>
                      <li class="breadcrumb-item active">Enter Results</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading request details...</p>
            </div>

            <div v-else class="row">
              <!-- Left Column: Request & Patient Info -->
              <div class="col-lg-4">
                <!-- Patient Card -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-account me-2 text-primary"></i>Patient Information</h6>
                    <div class="d-flex align-items-center mb-3">
                      <div class="avatar-md me-3">
                        <span class="avatar-title rounded-circle" :class="request.gender === 'Male' ? 'bg-primary' : 'bg-pink'" style="font-size: 1.2rem;">
                          {{ getInitials(request.patientName) }}
                        </span>
                      </div>
                      <div>
                        <h5 class="mb-1">{{ request.patientName }}</h5>
                        <span class="text-muted">{{ request.gender }} • {{ request.age }} yrs</span>
                      </div>
                    </div>
                    
                    <div class="row g-3">
                      <div class="col-6">
                        <small class="text-muted d-block">Patient ID</small>
                        <span>{{ request.patientID }}</span>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Blood Group</small>
                        <span>{{ request.bloodGroup || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Request Details Card -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-clipboard-text me-2 text-primary"></i>Request Details</h6>
                    
                    <div class="mb-3">
                      <small class="text-muted d-block">Request ID</small>
                      <span class="fw-medium text-primary">{{ request.requestID }}</span>
                    </div>

                    <div class="mb-3">
                      <small class="text-muted d-block">Test Name</small>
                      <span class="fw-medium">{{ request.testName }}</span>
                    </div>

                    <div class="row g-3 mb-3">
                      <div class="col-6">
                        <small class="text-muted d-block">Sample ID</small>
                        <span class="badge bg-info">{{ request.sampleID }}</span>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Priority</small>
                        <span class="badge" :class="getPriorityBadge(request.priority)">{{ request.priority }}</span>
                      </div>
                    </div>

                    <div class="row g-3 mb-3">
                      <div class="col-6">
                        <small class="text-muted d-block">Sample Type</small>
                        <span>{{ request.sampleType || '-' }}</span>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Turnaround</small>
                        <span>{{ request.turnaroundTime || '-' }}</span>
                      </div>
                    </div>

                    <div v-if="request.clinicalInfo" class="mb-0">
                      <small class="text-muted d-block">Clinical Information</small>
                      <p class="mb-0 small">{{ request.clinicalInfo }}</p>
                    </div>
                  </div>
                </div>

                <!-- Reference Ranges -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-chart-bell-curve me-2 text-primary"></i>Reference Ranges</h6>
                    <div class="row g-3">
                      <div class="col-12">
                        <small class="text-muted d-block">Male</small>
                        <span>{{ request.normalRangeMale || 'Not specified' }}</span>
                      </div>
                      <div class="col-12">
                        <small class="text-muted d-block">Female</small>
                        <span>{{ request.normalRangeFemale || 'Not specified' }}</span>
                      </div>
                      <div class="col-12">
                        <small class="text-muted d-block">Unit</small>
                        <span>{{ request.unit || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column: Results Entry Form -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 class="mb-1"><i class="mdi mdi-flask me-2 text-primary"></i>Test Results</h5>
                        <small class="text-muted">Enter the results for {{ request.testName }}</small>
                      </div>
                      <span class="badge bg-info">{{ request.sampleID }}</span>
                    </div>

                    <form @submit.prevent="saveResult">
                      <div class="row g-3">
                        <!-- Result Value -->
                        <div class="col-md-6">
                          <label class="form-label">Result <span class="text-danger">*</span></label>
                          <input type="text" class="form-control form-control-lg" v-model="resultForm.result" 
                                 placeholder="Enter result value" required>
                        </div>

                        <!-- Numeric Result (Optional) -->
                        <div class="col-md-6">
                          <label class="form-label">Numeric Value</label>
                          <div class="input-group">
                            <input type="number" step="0.01" class="form-control form-control-lg" v-model="resultForm.resultNumeric" 
                                   placeholder="Numeric value">
                            <span class="input-group-text">{{ request.unit || 'unit' }}</span>
                          </div>
                        </div>

                        <!-- Unit -->
                        <div class="col-md-4">
                          <label class="form-label">Unit</label>
                          <input type="text" class="form-control" v-model="resultForm.unit" :placeholder="request.unit || 'Unit'">
                        </div>

                        <!-- Normal Range -->
                        <div class="col-md-4">
                          <label class="form-label">Normal Range</label>
                          <input type="text" class="form-control" v-model="resultForm.normalRange" 
                                 :placeholder="request.gender === 'Male' ? request.normalRangeMale : request.normalRangeFemale">
                        </div>

                        <!-- Flag -->
                        <div class="col-md-4">
                          <label class="form-label">Flag</label>
                          <select class="form-select" v-model="resultForm.flag">
                            <option value="">Normal</option>
                            <option value="High">High (H)</option>
                            <option value="Low">Low (L)</option>
                            <option value="Critical High">Critical High (HH)</option>
                            <option value="Critical Low">Critical Low (LL)</option>
                            <option value="Abnormal">Abnormal (A)</option>
                          </select>
                        </div>

                        <!-- Interpretation -->
                        <div class="col-12">
                          <label class="form-label">Interpretation</label>
                          <textarea class="form-control" rows="3" v-model="resultForm.interpretation" 
                                    placeholder="Clinical interpretation of the result..."></textarea>
                        </div>

                        <!-- Comments -->
                        <div class="col-12">
                          <label class="form-label">Comments / Notes</label>
                          <textarea class="form-control" rows="2" v-model="resultForm.comments" 
                                    placeholder="Additional comments..."></textarea>
                        </div>
                      </div>

                      <!-- Flag Alert -->
                      <div v-if="resultForm.flag && resultForm.flag.includes('Critical')" class="alert alert-danger mt-3">
                        <i class="mdi mdi-alert-circle me-2"></i>
                        <strong>Critical Value Alert!</strong> This result requires immediate notification to the ordering physician.
                      </div>

                      <!-- Action Buttons -->
                      <div class="d-flex justify-content-between align-items-center pt-4 border-top mt-4">
                        <button type="button" class="btn btn-light" @click="goTo('lab-dashboard')">
                          <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
                        </button>
                        <div class="d-flex gap-2">
                          <button type="button" class="btn btn-outline-primary" @click="clearForm">
                            <i class="mdi mdi-refresh me-1"></i> Clear
                          </button>
                          <button type="submit" class="btn btn-success" :disabled="!resultForm.result || saving">
                            <span v-if="saving"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
                            <span v-else><i class="mdi mdi-check me-1"></i> Save Result</span>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <!-- Existing Results (if any) -->
                <div v-if="existingResults.length > 0" class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-history me-2 text-primary"></i>Previous Results</h6>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Result</th>
                            <th>Flag</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Technician</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="res in existingResults" :key="res.resultID">
                            <td><strong>{{ res.result }}</strong> {{ res.unit }}</td>
                            <td><span v-if="res.flag" class="badge" :class="getFlagBadge(res.flag)">{{ res.flag }}</span></td>
                            <td>{{ formatDateTime(res.resultDate) }}</td>
                            <td><span class="badge" :class="res.status === 'Verified' ? 'bg-success' : 'bg-warning'">{{ res.status }}</span></td>
                            <td>{{ res.labTechName }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "LabResults",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loading: true,
      saving: false,
      requestID: '',
      request: {},
      existingResults: [],
      
      resultForm: {
        result: '',
        resultNumeric: '',
        unit: '',
        normalRange: '',
        flag: '',
        interpretation: '',
        comments: ''
      },
      
      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      labTechID: ''
    };
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    showSuccess(message) {
      if (this.$refs.toast) {
        this.$refs.toast.success(message);
      } else {
        console.log('Success:', message);
      }
    },

    showError(message) {
      if (this.$refs.toast) {
        this.$refs.toast.error(message);
      } else {
        console.error('Error:', message);
        alert(message);
      }
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', { 
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
    },

    getPriorityBadge(priority) {
      switch (priority) {
        case 'STAT': return 'bg-danger';
        case 'Urgent': return 'bg-warning text-dark';
        default: return 'bg-secondary';
      }
    },

    getFlagBadge(flag) {
      if (!flag) return 'bg-success';
      if (flag.includes('Critical')) return 'bg-danger';
      if (flag === 'High' || flag === 'Low') return 'bg-warning text-dark';
      return 'bg-info';
    },

    loadRequest() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getLabRequest");
      data.append("requestID", this.requestID);

      execute(data, hospital)
        .then((res) => {
          this.loading = false;
          if (res.data.success && res.data.data.request) {
            this.request = res.data.data.request;
            this.existingResults = res.data.data.results || [];
            
            // Pre-fill unit and normal range
            this.resultForm.unit = this.request.unit || '';
            this.resultForm.normalRange = this.request.gender === 'Male' 
              ? this.request.normalRangeMale 
              : this.request.normalRangeFemale;
          } else {
            this.showError('Lab request not found');
            this.goTo('lab-dashboard');
          }
        })
        .catch((e) => {
          this.loading = false;
          console.error(e);
          this.showError('Error loading request');
        });
    },

    clearForm() {
      this.resultForm = {
        result: '',
        resultNumeric: '',
        unit: this.request.unit || '',
        normalRange: this.request.gender === 'Male' ? this.request.normalRangeMale : this.request.normalRangeFemale,
        flag: '',
        interpretation: '',
        comments: ''
      };
    },

    saveResult() {
      if (!this.resultForm.result) {
        this.showError('Please enter the result value');
        return;
      }

      this.saving = true;

      const data = new FormData();
      data.append("function", "addLabResult");
      data.append("requestID", this.requestID);
      data.append("result", this.resultForm.result);
      data.append("resultNumeric", this.resultForm.resultNumeric || '');
      data.append("unit", this.resultForm.unit);
      data.append("normalRange", this.resultForm.normalRange);
      data.append("flag", this.resultForm.flag);
      data.append("interpretation", this.resultForm.interpretation);
      data.append("comments", this.resultForm.comments);
      data.append("labTechID", this.labTechID);
      data.append("labTechName", this.sessionNames);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
        .then((res) => {
          this.saving = false;
          if (res.data.success) {
            this.showSuccess('Lab result saved successfully');
            setTimeout(() => {
              this.goTo('lab-dashboard');
            }, 1500);
          } else {
            this.showError(res.data.message || 'Failed to save result');
          }
        })
        .catch((e) => {
          this.saving = false;
          console.error(e);
          this.showError('Error saving result');
        });
    }
  },
  mounted() {
    this.requestID = this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.labTechID = sessionStorage.getItem("staffID") || sessionStorage.getItem("userID") || '';

    if (this.requestID) {
      this.loadRequest();
    } else {
      this.showError('No request specified');
      this.goTo('lab-dashboard');
    }
  }
};
</script>

<style scoped>
.avatar-md { width: 4rem; height: 4rem; }
.avatar-title { 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  width: 100%; 
  height: 100%; 
  font-weight: 600; 
  color: #fff;
}

.bg-pink { background-color: #e83e8c !important; }

.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.form-control-lg {
  font-size: 1.1rem;
}
</style>
